// React, Router, Permissions & Services
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import partCategoryService from "services/part-category-service";
import { AuthContext } from "context";

// Material Dashboard & MUI Components
import DeleteForeverTwoTone from "@mui/icons-material/DeleteForeverTwoTone";
import SettingsBackupRestoreTwoToneIcon from '@mui/icons-material/SettingsBackupRestoreTwoTone';
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";
import ConfirmDialog from "e4acomponents/ConfirmDialog";


function ArchivedPartCategories() {

    // Data
    const [partCategories, setPartCategories] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        title: "",
        prompt: "",
        onClose: () => { }
    });
    const [openDialog, setOpenDialog] = useState(false);

    const navigate = useNavigate();
    // Permissions
    const ability = useAbility(AbilityContext);
    const authContext = useContext(AuthContext);

    const restorePrompt = "Are you sure you want to restore this part category?";
    const restoreTitle = "Restore Part Category";
    const deletePrompt = "Are you sure you want to permanently delete this part category? This action cannot be undone.";
    const deleteTitle = "Permanently Delete Part Category";

    // Fetch part codes
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
            const response = await partCategoryService.getPartCategoriesByStatus("archived");
            setPartCategories(response.data.attributes);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
        })();
    }, []);

    useEffect(() => {
        setTableData(getRows(partCategories));
    }, [partCategories]);

    // Format data for DataTable
    const getRows = (info) => {
        let updatedInfo = info.map((element) => {
            return {
                categoryCode: "E4A-" + element.categoryCode + "-#####-#",
                parentCategory: element.parentCategory,
                description: element.description,
                examples: element.examples,
                status: element.status,
                id: element._id
            };
        });
        return updatedInfo
    }

    const clickRestoreHandler = async (id) => {
        try {
            const payload = {
                data: {
                    attributes: {
                        status: "Pending Approval"
                    },
                    logs: {
                        user: await authContext.getCurrentUser(),
                        comment: ""
                    }
                }
            }
            await partCategoryService.editStatus(id, payload);
            // Remove the restored part category from the table
            setPartCategories((prev) => prev.filter((item) => item._id !== id));
        } catch (error) {
            console.error(error);
        }
    }
    const clickDeleteHandler = async (id) => {
        const userId = await authContext.getCurrentUser();

        await partCategoryService.deletePartCategory(id, userId);
        // Remove the deleted part category from the table
        setPartCategories((prev) => prev.filter(item => item._id !== id));

    };

    const onClose = async (response, title, id) => {
        // Handle dialog close response if needed
        if (response) {
            console.log("Dialog confirmed action for:", title);
            if (title === restoreTitle) {
                console.log("Restoring item with ID:", id);
                await clickRestoreHandler(id);
            } else if (title === deleteTitle) {
                console.log("Deleting item with ID:", id);
                await clickDeleteHandler(id);
            }
        }
        setOpenDialog(false);
    };

    const handleOpenDialog = (title, prompt, id) => {
        setDialogProps({
            title: title,
            prompt: prompt,
            onClose: onClose,
            id: id
        })
        setOpenDialog(true)
    };

    // Array containing the dataTable's headers and rows
    const dataTableData = {
        columns: [
            { Header: "Part Category", accessor: "categoryCode", width: "20%" },
            { Header: "Parent Category", accessor: "parentCategory", width: "20%" },
            { Header: "Description", accessor: "description", width: "20%" },
            { Header: "Example and Notes", accessor: "examples", width: "30%" },
            {
                Header: "Actions",
                disableSortBy: true,
                accessor: "actions",
                Cell: (info) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            <Tooltip
                                title="Permanently Delete"
                                onClick={() => handleOpenDialog(deleteTitle, deletePrompt, info.cell.row.original.id)}
                            >
                                <IconButton>
                                    <DeleteForeverTwoTone color="error" />
                                </IconButton>
                            </Tooltip>

                            <Tooltip
                                title="Restore"
                                onClick={() => handleOpenDialog(restoreTitle, restorePrompt, info.cell.row.original.id)}
                            >
                                <IconButton>
                                    <SettingsBackupRestoreTwoToneIcon color="success" />
                                </IconButton>
                            </Tooltip>

                        </MDBox>

                    );
                },
            }
        ],
        rows: tableData
    };

    return (
        <DashboardLayout>

            <ConfirmDialog
                open={openDialog}
                props={dialogProps}
            />

            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                <Card>

                    {/* Page Title */}
                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
                        <MDTypography variant="h5" fontWeight="medium">
                            Archived Part Categories
                        </MDTypography>
                    </MDBox>

                    {/* Data Table */}
                    <MDBox>
                        <DataTable
                            table={dataTableData}
                            canSearch
                            customRowProps={(row, index) => ({
                                style: { '--row-index': index }
                            })}
                            isLoading={isLoading}
                            tableId="archived-part-categories-table"
                        />
                    </MDBox>

                </Card>
            </MDBox>

            <MDBox>
                <Footer />
            </MDBox>

        </DashboardLayout>
    );
}

export default ArchivedPartCategories;
